import { z } from 'zod';
import { zPaymentGateway, zPaymentMethodId } from './enums';

const paymentRefundSchema = z.object({
  amount: z.number(),
  refundedAt: z.number(),
  paymentGatewayRefundId: z.string().nullable().default(null),
  status: z.literal(['SUCCESS', 'PENDING', 'FAILED']),
  errorCode: z.string().nullable().default(null),
  errorMessage: z.string().nullable().default(null),
});

const paymentSchema = z.object({
  last4: z.string(),
  amount: z.number(),
  paidAt: z.number(),
  paymentGateway: zPaymentGateway.nullable().default(null),
  paymentGatewayAccountId: z.string().nullable().default(null),
  paymentGatewayPaymentId: z.string().nullable().default(null),
  paymentGatewayPaymentMethodId: z.string().nullable().default(null),
  paymentMethodId: zPaymentMethodId.nullable().default(null),
  refunds: z.array(paymentRefundSchema).default([]),
});

export const sessionSchema = z.object({
  companyCode: z.string(),
  locationCode: z.string(),
  siteCode: z.string(),

  firstName: z.string(),
  lastName: z.string(),
  mobileNumber: z.string(),
  /**
   * Don't require the user to provide an email address as we use SMS for all notifications.
   */
  email: z.string().nullable().default(null),
  /**
   * Optional estimated checkout date provided at registration.
   * Stored as an ISO date string (e.g. "2026-03-15").
   */
  estimatedCheckoutDate: z.string().nullable().default(null),
  payments: z.array(paymentSchema).default([]),

  /**
   * The amount of electricity used in kWh.
   * To calculate total usage, Math.round(electricityUnits * electricityUnitPrice)
   */
  electricityUnits: z.number().nullable().default(null),
  electricityUnitsUpdatedAt: z.number().nullable().default(null),
  /**
   * Snapshot of `location.electricityUnitPrice` at session creation time.
   * All usage calculations use this value so that mid-stay price changes
   * on the location do not affect existing sessions.
   */
  electricityUnitPrice: z.number(),

  waterUnits: z.number().nullable().default(null),
  waterUnitsUpdatedAt: z.number().nullable().default(null),
  /**
   * Snapshot of `location.waterUnitPrice` at session creation time.
   * All usage calculations use this value so that mid-stay price changes
   * on the location do not affect existing sessions.
   */
  waterUnitPrice: z.number(),

  /**
   * The OpenMeterLastReadDate value from the Electricity MeterReading in the
   * Orto-Mate API /meterreadings response. Stored once and never updated.
   * Used to validate that payments are associated with the correct on-premise
   * device session. Example: "2026-03-06T16:23:02"
   */
  ortomateSessionId: z.string().nullable().default(null),

  /**
   * On checkin (after a successful deposit payment), we take the meter reading.
   */
  meterReadStart: z.number().nullable().default(null),
  /**
   * Timestamp derived from ortomateSessionId via naiveDateToUtcMs().
   */
  meterReadStartAt: z.number().nullable().default(null),
  /**
   * On checkout, we capture and store the meter reading.
   */
  meterReadEnd: z.number().nullable().default(null),
  /**
   * Timestamp when the final "end" read was taken.
   */
  meterReadEndAt: z.number().nullable().default(null),

  /**
   * When the guest accepted the company's terms and conditions at registration.
   * Null when the company had no terms configured, so this doubles as the record
   * of whether the acceptance gate applied to this session.
   */
  termsAcceptedAt: z.number().nullable().optional().default(null),

  smsLinkSentAt: z.number().nullable().default(null),

  /**
   * Automated hourly balance enforcement state.
   * These fields are internal-only and drive idempotency/escalation logic.
   */
  lastAutomatedBalanceCheckAt: z.number().nullable().default(null),
  lastLowBalanceAlertAt: z.number().nullable().default(null),
  emptyBalanceAlertAt: z.number().nullable().default(null),
  powerDisconnectedAt: z.number().nullable().default(null),
  powerDisconnectEmailSentAt: z.number().nullable().default(null),

  /**
   * Timestamp of the checkout-day reminder SMS (sent at 10am Australia/Melbourne
   * on `estimatedCheckoutDate`). Null until sent; sent at most once per session.
   * If `estimatedCheckoutDate` ever becomes editable, switch to a
   * `checkoutReminderSentForDate` (string) comparison instead.
   */
  checkoutReminderSentAt: z.number().nullable().default(null),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
  /**
   * This session UUID is generated when a new session is created.
   * It will form part of the long lived URL that will form part of the users session.
   *
   * Example: https://www.orto-matepay.com/SMATCB/A01?s=SESSION_UUID
   *
   * If a user visits https://www.orto-matepay.com/SMATCB/A01 (without the session UUID),
   * they will be prompted for their mobile number. Then sent a SMS containing the URL with
   * the session UUID included.
   */
  sessionUuid: z.uuidv4().optional(),
});
export type SessionSchema = z.output<typeof sessionSchema>;

export const mobileNumberInputSchema = z
  .string()
  .trim()
  .min(1, 'Mobile number is required')
  .transform((val) => val.replace(/\s/g, ''))
  .refine(
    (val) => {
      if (val.startsWith('+')) return /^\+[1-9]\d{1,14}$/.test(val);
      return /^04\d{8}$/.test(val);
    },
    { message: 'Invalid mobile number' },
  )
  .transform((val) => {
    if (val.startsWith('+')) return val;
    return `+61${val.slice(1)}`;
  })
  .pipe(z.e164());

export const sessionCvxSchema = sessionSchema.safeExtend({
  sessionUuid: z.uuidv4(),
  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
});
export type SessionCvx = z.output<typeof sessionCvxSchema>;

//
// User form
//

export const sessionFormSchema = sessionSchema
  .pick({
    siteCode: true,
    firstName: true,
    lastName: true,
    mobileNumber: true,
    email: true,
    estimatedCheckoutDate: true,
  })
  .extend({
    companyLocationCode: z.string(),
    /**
     * Ticked by the guest when the company has terms and conditions configured.
     * Ignored (and left `false`) when it does not — `createSession` decides which
     * applies from the company record rather than trusting the client.
     */
    termsAccepted: z.boolean().default(false),
  });
export type SessionForm = z.output<typeof sessionFormSchema>;

export const sessionFormStrictSchema = sessionFormSchema.safeExtend({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  mobileNumber: mobileNumberInputSchema,
  email: z.email().nullable().default(null),
  estimatedCheckoutDate: z.string().date().nullable().default(null),
});
